
import React from 'react';
import { Icons } from '../components/ui/Icons.tsx';

interface TermsPageProps {
  onClose?: () => void;
}

const TermsPage: React.FC<TermsPageProps> = ({ onClose }) => {
  return (
    <div className="w-full h-full flex flex-col bg-white animate-in fade-in duration-500 overflow-y-auto no-scrollbar">
      {/* Navigation / Header Area */}
      <div className="flex items-center justify-between px-10 py-8 sticky top-0 bg-white/80 backdrop-blur-md z-10 border-b border-gray-100">
        <div className="flex items-center gap-2">
           <Icons.Logo className="w-8 h-8" />
           <span className="text-xl font-bold tracking-tight">Neura AI</span>
        </div>
        {onClose && (
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-900 hover:bg-gray-100 rounded-full transition-all"
          >
            <Icons.X className="w-6 h-6" />
          </button>
        )}
      </div>

      <div className="max-w-4xl mx-auto px-10 py-16 w-full">
        <div className="mb-16">
          <h1 className="text-5xl font-bold text-gray-900 mb-4 tracking-tight">Terms of Service</h1>
          <p className="text-lg text-gray-500 uppercase tracking-widest font-semibold">Last Updated: March 30, 2026</p>
        </div>

        <div className="prose prose-gray max-w-none">
          <p className="text-xl text-gray-600 leading-relaxed mb-12">
            Welcome to <strong>Neura AI</strong> ("Neura", "we", "us", or "our"). These Terms of Service ("Terms") govern your access to and use of Neura’s AI-powered automation platform, including all features, tools, APIs, and services (collectively, the "Service").
          </p>

          <div className="space-y-12">
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Overview of Service</h2>
              <p className="text-gray-600 leading-relaxed">
                Neura AI provides an intelligent automation platform that allows users to perform tasks, manage workflows, and interact with third-party tools using natural language commands and AI agents.
              </p>
              <p className="text-gray-600 leading-relaxed mt-4">
                We may update, modify, or discontinue features at any time without prior notice.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">2. Accounts & Access</h2>
              <ul className="list-disc pl-6 space-y-2 text-gray-600">
                <li>You must provide accurate and complete information when creating an account.</li>
                <li>You are responsible for maintaining account security.</li>
                <li>You are liable for all activities under your account.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">3. Subscriptions & Billing</h2>
              <ul className="list-disc pl-6 space-y-2 text-gray-600">
                <li>Neura operates on a subscription-based model.</li>
                <li>Subscriptions auto-renew unless canceled.</li>
              <li>Pricing may change at any time.</li>
              </ul>
              <div className="mt-6 p-6 bg-gray-50 rounded-2xl border border-gray-100">
                <h4 className="font-bold text-gray-900 mb-3">Payments:</h4>
                <ul className="list-disc pl-6 space-y-2 text-gray-600 font-medium">
                  <li>All payments are non-refundable unless required by law.</li>
                  <li>Usage credits (if applicable) expire at the end of the billing cycle.</li>
                  <li>Failure to pay may result in suspension or termination.</li>
                </ul>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">4. Acceptable Use</h2>
              <p className="text-gray-600 mb-4 font-medium italic">You agree NOT to use Neura to:</p>
              <ul className="list-disc pl-6 space-y-2 text-gray-600 shadow-sm border-l-2 p-4">
                <li>Violate laws or regulations</li>
                <li>Infringe intellectual property rights</li>
                <li>Generate harmful, abusive, or illegal content</li>
                <li>Attempt unauthorized system access</li>
                <li>Disrupt or overload the platform</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4 border-l-4 border-yellow-400 pl-4">5. AI Behavior Disclaimer</h2>
              <p className="text-gray-600 leading-relaxed">
                Neura uses AI systems to automate tasks and generate outputs.
              </p>
              <ul className="list-disc pl-6 mt-4 space-y-2 text-gray-600 font-medium underline-offset-4">
                <li>AI responses may be inaccurate or incomplete.</li>
                <li>You are responsible for reviewing outputs before use.</li>
                <li>Neura is not liable for decisions made based on AI-generated results.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Third-Party Integrations</h2>
              <p className="text-gray-600 leading-relaxed font-semibold">
                Neura integrates with third-party services (e.g., Gmail, Slack, Notion).
              </p>
              <ul className="list-disc pl-6 mt-4 space-y-2 text-gray-600">
                <li>You authorize Neura to access these services on your behalf.</li>
                <li>We are not responsible for third-party service failures, changes, or data handling.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">7. User Data & Privacy</h2>
              <ul className="list-disc pl-6 space-y-2 text-gray-600">
                <li>You retain ownership of your data ("User Data").</li>
                <li>We process data only to provide and improve the Service.</li>
                <li>You must have rights to any data you upload or connect.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">8. Intellectual Property</h2>
              <ul className="list-disc pl-6 space-y-2 text-gray-600">
                <li>All Neura software, branding, and technology remain our property.</li>
                <li>You are granted a limited, non-exclusive, non-transferable license to use the Service.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4 text-red-600 underline">9. Termination</h2>
              <p className="text-gray-600 mb-4">We may suspend or terminate your account at any time for violations of these Terms, non-payment, or security risks.</p>
              <div className="p-4 bg-red-50 border border-red-100 rounded-xl text-red-800 text-sm italic">
                 Upon termination, access is revoked immediately and data may be deleted without notice.
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">10. Disclaimer of Warranties</h2>
              <p className="text-gray-600 leading-relaxed font-bold mb-4">The Service is provided "as is" and "as available".</p>
              <p className="text-gray-600 mb-2">We do not guarantee:</p>
              <ul className="list-disc pl-6 space-y-2 text-gray-600">
                <li>Accuracy of AI outputs</li>
                <li>Continuous availability</li>
                <li>Error-free performance</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">11. Limitation of Liability</h2>
              <p className="text-gray-600 leading-relaxed italic mb-4">To the maximum extent permitted by law:</p>
              <p className="text-gray-600 mb-2 font-medium">Neura shall not be liable for:</p>
              <ul className="list-disc pl-6 space-y-2 text-gray-600 mb-6">
                 <li>Indirect or consequential damages</li>
                 <li>Loss of data, profits, or business</li>
              </ul>
              <div className="inline-block px-4 py-2 bg-gray-100 rounded-full font-bold text-black text-sm">
                 Total liability is limited to the amount paid by you in the last 30 days.
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">12. Indemnification</h2>
              <p className="text-gray-600 mb-2">You agree to indemnify and hold Neura harmless from any claims resulting from:</p>
              <ul className="list-disc pl-6 space-y-2 text-gray-600 font-medium">
                <li>Your use of the Service</li>
                <li>Violations of these Terms</li>
                <li>Misuse of AI outputs</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4 lowercase tracking-widest bg-black text-white inline-block px-4 py-1 rounded">13. Governing Law</h2>
              <p className="text-gray-600 leading-relaxed mt-4">
                These Terms are governed by applicable laws in your operating jurisdiction.
              </p>
              <p className="text-gray-900 font-bold mt-2 italic shadow-sm">
                Disputes will be resolved through binding arbitration.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">14. Changes to Terms</h2>
              <p className="text-gray-600 leading-relaxed font-medium">
                We may update these Terms at any time. Continued use of Neura means you accept the updated Terms.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">15. Contact</h2>
              <div className="p-8 bg-gray-900 text-white rounded-3xl border border-gray-100 flex flex-col md:flex-row items-center justify-between gap-6 overflow-hidden relative">
                 <div className="relative z-10">
                    <h4 className="text-xl font-bold mb-2">Need help with these Terms?</h4>
                    <p className="text-gray-400 text-sm">Our team is available to clarify any points regarding our service agreement.</p>
                 </div>
                 <a href="mailto:useneuraai@gmail.com" className="relative z-10 px-8 py-3 bg-white text-black rounded-full font-bold hover:bg-gray-200 transition-all shadow-lg active:scale-95">
                    useneuraai@gmail.com
                 </a>
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsPage;
